/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CustomLayout from "../../Core/Layout/CustomLayout";
import RequestDetailsDrawer from "./RequestDetailsDrawer";
import { updateRequest } from "../../Redux/Slices/requests.slice";
import RejectRequestModal from "./RejectRequestModal";
import { getUserByEmail } from "../../Redux/APIs/systemUsersAPI";
import ToastMessageModal from "./ToastMessageModal";
import { getAllTrikesV3 } from "../../Redux/APIs/trikesAPI";
import { getAllBatteriesV3 } from "../../Redux/APIs/batteriesAPI";
import UssdList from "./Ussd/UssdList";
import RentalList from "./Rentals/RentalList";
import Header from "./Header";
import useDebounce from "../../Core/hooks/useDebounce";
import { getRequestList } from "../../Redux/APIs/requestsAPI";
import { getRentalRequest } from "../../Redux/APIs/rentalsAPI";
import { updateRental } from "../../Redux/Slices/rentals.slice";
import UnauthorizedAccess from "../../Core/Components/UnauthorizedAccess";
import { DEFAULT_LIMIT } from "../../Utils/constants";

const RequestList = () => {
  const dispatch = useDispatch();

  const [searchParams, setSearchParams] = useSearchParams();

  const { requestDetailsVisible, rejectModal, showToastMessage } = useSelector(
    (state) => state.requests,
  );

  const { userDetails } = useSelector((state) => state.loggedInUser);

  const isSongaMobilityUser = userDetails?.isSongaMobilityUser;

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_LIMIT);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    hub: [],
    type: [],
    status: [],
  });

  const selectedTab = searchParams.get("tab");
  const debounceSearch = useDebounce(search, 500);

  const onPageChange = () => {
    dispatch(
      updateRequest({
        key: "requestDetailsVisible",
        value: false,
      }),
    );

    dispatch(
      updateRequest({
        key: "transportDetailStep",
        value: 1,
      }),
    );

    dispatch(
      updateRequest({
        key: "currentRequest",
        value: {},
      }),
    );

    dispatch(
      updateRental({
        key: "currentRental",
        value: {},
      }),
    );

    dispatch(
      updateRequest({
        key: "rentalAssigning",
        value: {
          trikeId: 0,
          trikeRegistrationNo: "",
          vehicleType: "",
          batteries: [],
        },
      }),
    );
  };

  const getBody = () => {
    switch (selectedTab) {
      case "ussd": {
        if (!isSongaMobilityUser) {
          return <UnauthorizedAccess className="w-[100%] h-[100%]" />;
        }

        return (
          <UssdList
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        );
      }

      case "rentals":
        return (
          <RentalList
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        );

      default:
        return <div />;
    }
  };

  // set the search params
  useEffect(() => {
    if (!searchParams.has("tab")) {
      if (isSongaMobilityUser) searchParams.set("tab", "ussd");
      else searchParams.set("tab", "rentals");

      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  // fetch user details (to get permissions on every refresh), trikes and batteries
  useEffect(() => {
    dispatch(getUserByEmail({ email: userDetails.email }));
    dispatch(getAllTrikesV3());
    dispatch(getAllBatteriesV3());
  }, [dispatch]);

  // clear the toast message
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (showToastMessage?.visible) {
        dispatch(
          updateRequest({
            key: "showToastMessage",
            value: {
              visible: false,
              data: {},
            },
          }),
        );
      }
    }, 5000);

    return () => clearTimeout(timeout);
  }, [dispatch, showToastMessage]);

  // get ussd request list or rental request list with filters
  useEffect(() => {
    const filterObj = {
      limit: pageSize,
      offset: ((currentPage || 1) - 1) * pageSize,
    };

    if (debounceSearch) filterObj.searchQuery = debounceSearch;
    if (filters.hub.length) filterObj.hub = filters.hub;
    if (filters.status.length) filterObj.status = filters.status;

    // fetch ussd
    if (selectedTab === "ussd" && isSongaMobilityUser) {
      if (filters.type.length) filterObj.type = filters.type;
      dispatch(getRequestList(filterObj));
    }

    // fetch rentals
    if (selectedTab === "rentals") {
      dispatch(getRentalRequest(filterObj));
    }
  }, [dispatch, currentPage, pageSize, debounceSearch, filters, selectedTab]);

  // clear all the states
  useEffect(() => {
    return () => {
      onPageChange();
    };
  }, []);

  return (
    <CustomLayout pageName="Requests">
      <Header
        filters={filters}
        setFilters={setFilters}
        search={search}
        setSearch={setSearch}
        setCurrentPage={setCurrentPage}
      />

      {getBody()}

      <RequestDetailsDrawer
        visible={requestDetailsVisible}
        onCancel={() => {
          dispatch(
            updateRequest({
              key: "requestDetailsVisible",
              value: false,
            }),
          );

          dispatch(
            updateRequest({
              key: "transportDetailStep",
              value: 1,
            }),
          );

          dispatch(
            updateRequest({
              key: "currentRequest",
              value: {},
            }),
          );

          dispatch(
            updateRental({
              key: "currentRental",
              value: {},
            }),
          );

          dispatch(
            updateRequest({
              key: "rentalAssigning",
              value: {
                trikeId: 0,
                trikeRegistrationNo: "",
                vehicleType: "",
                batteries: [],
              },
            }),
          );
        }}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      />

      <RejectRequestModal
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        visible={rejectModal?.visible}
        onCancel={() => {
          dispatch(
            updateRequest({
              key: "rejectModal",
              value: {
                visible: false,
                type: "ussd",
              },
            }),
          );

          dispatch(
            updateRequest({
              key: "requestDetailsVisible",
              value: false,
            }),
          );
        }}
      />

      <ToastMessageModal
        visible={showToastMessage?.visible}
        onCancel={() =>
          dispatch(
            updateRequest({
              key: "showToastMessage",
              value: {
                visible: false,
                data: {},
              },
            }),
          )
        }
        title={showToastMessage?.data?.title}
        description={showToastMessage?.data?.description}
      />
    </CustomLayout>
  );
};

export default RequestList;
